import { extract } from './extract-util.js';
import { globalSprinkles } from './safe-sprinkles.js';
import type {
  MarginProperties,
  SpacingProperties,
} from './sprinkle-properties.js';

/**
 * Defines the box spacing props.
 * @public
 */
export interface SpacingProps {
  /** Defines CSS padding property */
  padding?: SpacingProperties;
  /** Defines CSS padding property */
  p?: SpacingProperties;

  /** Defines CSS x padding property */
  paddingX?: SpacingProperties;
  /** Defines CSS x padding property */
  px?: SpacingProperties;

  /** Defines CSS y padding property */
  paddingY?: SpacingProperties;
  /** Defines CSS y padding property */
  py?: SpacingProperties;

  /** Defines CSS top padding property */
  paddingTop?: SpacingProperties;
  /** Defines CSS top padding property */
  pt?: SpacingProperties;

  /** Defines CSS right padding property */
  paddingRight?: SpacingProperties;
  /** Defines CSS right padding property */
  pr?: SpacingProperties;

  /** Defines CSS bottom padding property */
  paddingBottom?: SpacingProperties;
  /** Defines CSS bottom padding property */
  pb?: SpacingProperties;

  /** Defines CSS left padding property */
  paddingLeft?: SpacingProperties;
  /** Defines CSS left padding property */
  pl?: SpacingProperties;

  /** Defines CSS margin property */
  margin?: MarginProperties;
  /** Defines CSS margin property */
  m?: MarginProperties;

  /** Defines CSS x margin property */
  marginX?: MarginProperties;
  /** Defines CSS x margin property */
  mx?: MarginProperties;

  /** Defines CSS y margin property */
  marginY?: MarginProperties;
  /** Definesy CSS margin property */
  my?: MarginProperties;

  /** Defines CSS top margin property */
  marginTop?: MarginProperties;
  /** Defines CSS top margin property */
  mt?: MarginProperties;

  /** Defines CSS right margin property */
  marginRight?: MarginProperties;
  /** Defines CSS right margin property */
  mr?: MarginProperties;

  /** Defines CSS bottom margin property */
  marginBottom?: MarginProperties;
  /** Defines bottom margin property */
  mb?: MarginProperties;

  /** Defines CSS left margin property */
  marginLeft?: MarginProperties;
  /** Defines CSS left margin property */
  ml?: MarginProperties;
}

/**
 * Defines the defaults for spacings
 * @internal
 */
export type DefaultSpacingProps = Pick<
  SpacingProps,
  | 'padding'
  | 'paddingX'
  | 'paddingY'
  | 'paddingTop'
  | 'paddingRight'
  | 'paddingBottom'
  | 'paddingLeft'
  | 'margin'
  | 'marginX'
  | 'marginY'
  | 'marginTop'
  | 'marginRight'
  | 'marginBottom'
  | 'marginLeft'
>;

/**
 * Keys that will define the spacing of a component.
 * @public
 */
const spacingPropKeys: Array<keyof SpacingProps> = [
  'padding',
  'p',
  'paddingX',
  'px',
  'paddingY',
  'py',
  'paddingTop',
  'pt',
  'paddingRight',
  'pr',
  'paddingBottom',
  'pb',
  'paddingLeft',
  'pl',
  'margin',
  'm',
  'marginX',
  'mx',
  'marginY',
  'my',
  'marginTop',
  'mt',
  'marginRight',
  'mr',
  'marginBottom',
  'mb',
  'marginLeft',
  'ml',
];

/**
 * Given a set of props will return the relevant class names for the spacing sprinkles
 * and the remaining props.
 * @internal
 */
export function getSpacingSprinkles<T extends SpacingProps>(
  props: T,
  defaults: DefaultSpacingProps = {},
): [sprinklesClassNames: string, remainingProps: Omit<T, keyof SpacingProps>] {
  const { extracted, rest } = extract(props, spacingPropKeys);

  const spacingSprinkleConfig: Parameters<typeof globalSprinkles>[0] = {
    paddingTop:
      extracted.pt ??
      extracted.paddingTop ??
      defaults.paddingTop ??
      extracted.py ??
      extracted.paddingY ??
      defaults.paddingY ??
      extracted.p ??
      extracted.padding ??
      defaults.padding,
    paddingRight:
      extracted.pr ??
      extracted.paddingRight ??
      defaults.paddingRight ??
      extracted.px ??
      extracted.paddingX ??
      defaults.paddingX ??
      extracted.p ??
      extracted.padding ??
      defaults.padding,
    paddingBottom:
      extracted.pb ??
      extracted.paddingBottom ??
      defaults.paddingBottom ??
      extracted.py ??
      extracted.paddingY ??
      defaults.paddingY ??
      extracted.p ??
      extracted.padding ??
      defaults.padding,
    paddingLeft:
      extracted.pl ??
      extracted.paddingLeft ??
      defaults.paddingLeft ??
      extracted.px ??
      extracted.paddingX ??
      defaults.paddingX ??
      extracted.p ??
      extracted.padding ??
      defaults.padding,
    marginTop:
      extracted.mt ??
      extracted.marginTop ??
      defaults.marginTop ??
      extracted.my ??
      extracted.marginY ??
      defaults.marginY ??
      extracted.m ??
      extracted.margin ??
      defaults.margin,
    marginRight:
      extracted.mr ??
      extracted.marginRight ??
      defaults.marginRight ??
      extracted.mx ??
      extracted.marginX ??
      defaults.marginX ??
      extracted.m ??
      extracted.margin ??
      defaults.margin,
    marginBottom:
      extracted.mb ??
      extracted.marginBottom ??
      defaults.marginBottom ??
      extracted.my ??
      extracted.marginY ??
      defaults.marginY ??
      extracted.m ??
      extracted.margin ??
      defaults.margin,
    marginLeft:
      extracted.ml ??
      extracted.marginLeft ??
      defaults.marginLeft ??
      extracted.mx ??
      extracted.marginX ??
      defaults.marginX ??
      extracted.m ??
      extracted.margin ??
      defaults.margin,
  };

  return [globalSprinkles(spacingSprinkleConfig), rest];
}
